import * as React from 'react';
import type { IconType } from 'react-icons';
import { LuArrowRight, LuArrowUpRight, LuChevronDown } from 'react-icons/lu';

import { cn } from '@mds/shared/lib/utils';
import { useReveal } from '@mds/shared/hooks/use-reveal';

/**
 * How the markers are set. The same three words `IconCard` uses, for the same
 * reason — a mark on a block is either quiet, full-strength, or the brand's.
 *
 * Not called a tone: `Tone` in this repo means danger / warning / success /
 * neutral, and a second sense of the word is the drift `lib/tone.ts` exists to
 * prevent.
 */
const ACCENT = {
  muted: 'text-ink-muted',
  ink: 'text-ink',
  brand: 'text-brand-text',
} as const;

/**
 * The title at each size. `md` is a bold line at the lead size, a name read
 * ahead of its sentence rather than a heading over it; it caps the cell at 22
 * characters, the longest layout name. `lg` is a question set as a display
 * line, and a question broken across two lines to meet a cap would read as two
 * rows. A clear step under the chapter heading at every width (28px rising to
 * 44px), so a row never reads as the section's equal.
 */
const TITLE_SIZE = {
  md: 'max-w-[22ch] text-lg',
  lg: 'text-xl lg:text-2xl',
} as const;

/**
 * The column templates, written out whole so Tailwind sees every one — a class
 * assembled at runtime is a class it never generates.
 *
 * With an indicator the row keeps its columns on a phone as well: the arrow
 * belongs at the end of the line it points from, and stacked under the title
 * it would point at nothing.
 */
const COLUMNS = {
  titles: 'sm:grid-cols-[auto_auto_minmax(0,1fr)]',
  plain: 'sm:grid-cols-[auto_minmax(0,1fr)]',
  titlesIndicator: 'grid-cols-[auto_minmax(0,1fr)_auto] sm:grid-cols-[auto_auto_minmax(0,1fr)_auto]',
  plainIndicator: 'grid-cols-[auto_minmax(0,1fr)_auto]',
  /* Titles with no sentences to follow them — a list of questions. The title
   * takes the sentence's share instead of sitting in an `auto` track beside an
   * empty one, which cost the row a whole column gap and wrapped a question
   * that had room to stand on one line. */
  titlesOnlyIndicator: 'grid-cols-[auto_minmax(0,1fr)_auto]',
  /* `expand="active"`: the sentence goes under the title rather than beside
   * it, so the row is the marker and one column, at every width. */
  expandActive: 'grid-cols-[auto_minmax(0,1fr)]',
  expandActiveIndicator: 'grid-cols-[auto_minmax(0,1fr)_auto]',
} as const;

export type RuledListItem = {
  /**
   * The mono cell on the left — an ordinal, a time, a code. "01", "6:00 PM".
   *
   * The column sizes itself to the widest marker in the list, so a list of
   * times and a list of two-digit ordinals both get exactly the room they
   * need and every row lines up.
   */
  marker?: string;
  /**
   * A glyph before the marker — a calendar beside "Date". The component rather
   * than an element, so the list sets its size and colour.
   */
  icon?: IconType;
  /** The bold line — the name of the point. Omit it for a plain two-column row. */
  title?: string;
  /** The sentence. */
  description?: string;
  /**
   * Selectable lists only — the id of the panel this row shows, so the tab can
   * point at it with `aria-controls`.
   */
  controls?: string;
};

export type RuledListProps = {
  items: RuledListItem[];
  /** How the markers are set. */
  accent?: keyof typeof ACCENT;
  /**
   * The markers' weight. `regular` suits an agenda, where the sentence is the
   * point; `medium` is for a card of facts, where the label has to hold its own
   * beside the value — "Reference", "Date" read too faint at 12px otherwise.
   */
  markerWeight?: 'regular' | 'medium';
  /**
   * Where the sentence sits in its column.
   *
   * `start`, the default, is the agenda: a sentence reads best starting right
   * after its time. `end` is a card of short facts — "Date … Thu 17 Sept" —
   * where each value is read across the row against its label.
   *
   * From `sm` only, where the columns are. Below that the row stacks, and a
   * value pushed right alone on its own line would just look misplaced.
   */
  align?: 'start' | 'end';
  /**
   * Where the rules go.
   *
   * `all`, the default, rules above every row and under the last, and holds
   * every row to the height of the tallest — a table, read across.
   *
   * `between` rules only between rows, and lets each row be as tall as its
   * sentence. For a short set of statements beside a headline, where an open
   * top and bottom lets the list sit in the band rather than box itself off.
   *
   * Ignored by a selectable list, which rules under every row: the rule under
   * the selected one is the mark that says which it is.
   */
  rules?: 'all' | 'between';
  /** `relaxed` gives each row room to breathe, for sentences rather than facts. */
  spacing?: 'compact' | 'relaxed';
  /** How large the titles are. `lg` is a display line, for a list of questions. */
  titleSize?: keyof typeof TITLE_SIZE;
  /**
   * An arrow at the end of every row: a small ↗ at rest, and a filled round →
   * on the selected row of a selectable list.
   */
  indicator?: boolean;
  /**
   * Selectable lists only — which row is chosen. Controlled: the list holds no
   * state, so the page owns the index and whatever the rows switch reads the
   * same number.
   */
  activeIndex?: number;
  /**
   * Makes the list selectable. Every row becomes a tab, and this is called with
   * its index on a click, on a mouse hover, or when the arrow keys reach it.
   */
  onSelect?: (index: number) => void;
  /** Selectable lists only — names the set of tabs for a screen reader. */
  label?: string;
  /**
   * Selectable lists only — which rows show their sentence.
   *
   * `all`, the default, sets every sentence beside its title. `active` sets it
   * under the title and opens it on the chosen row alone, the others folded to
   * their titles: for a sequence of steps walked through one at a time, where
   * the sentence is the step and only one is being read.
   */
  expand?: 'all' | 'active';
  /**
   * Selectable lists only — whether resting the mouse on a row chooses it.
   *
   * On by default. Off for a list that moves on a timer, where a hover that
   * chose a row would pull the sequence out from under someone only moving the
   * mouse past it; the row then just leans in, to say it can be clicked.
   */
  selectOnHover?: boolean;
  /**
   * Draws a row's sentence in place of the plain text: a `Typewriter`, for
   * steps that type themselves out. Called for every row, open or not.
   */
  renderDescription?: (index: number) => React.ReactNode;
  /**
   * Selectable lists only — the panel for a row, drawn under it below `lg` so
   * the list reads as an accordion on a phone. The caller keeps its own panel
   * beside the list from `lg` and hides it below. With this set, the phone's
   * indicator is a chevron, down when shut and up when open, and tapping the
   * open row closes it.
   */
  renderPanel?: (index: number) => React.ReactNode;
  /**
   * A line of small print after the last row — a price's conditions under a
   * card of facts. Outside the rows, so it does not take a column or a rule.
   */
  note?: React.ReactNode;
  className?: string;
};

/**
 * A ruled list of rows: a mono marker, an optional bold title, and a sentence.
 *
 * Two shapes out of one component, and which one you get falls out of the
 * items rather than out of a prop:
 *
 *   markers + titles + text   three columns — a numbered set of points
 *   markers + text            two columns — a schedule, a spec table
 *
 * The decision is made for the whole list rather than per row, because the
 * columns are shared: one row quietly dropping to two columns would put its
 * text under the column above it's titles and break the alignment that is the
 * entire reason this is a grid.
 *
 * `ul` rather than `dl`, though the pairs are term-and-definition shaped. A
 * `dl` would be defensible for the two-column form, where the marker really is
 * the term — but in the three-column form the marker is an ordinal, which is
 * decoration, and the term is the title. One element that means two things
 * depending on how many columns there are is worse than a plain list.
 *
 * With `onSelect` it is a vertical tablist instead: the rows are the tabs, and
 * the panel they switch is the caller's, beside it. The rows keep exactly the
 * same columns, which is why this is a mode of the list and not a second
 * component that copies its grid.
 */
export default function RuledList({
  items,
  accent = 'muted',
  markerWeight = 'regular',
  align = 'start',
  rules = 'all',
  spacing = 'compact',
  titleSize = 'md',
  indicator = false,
  activeIndex,
  onSelect,
  label,
  expand = 'all',
  selectOnHover = true,
  renderDescription,
  renderPanel,
  note,
  className,
}: RuledListProps) {
  // Before the early return: a hook cannot be called conditionally, and an
  // empty list returning first would change how many run.
  const { ref, state } = useReveal<HTMLUListElement>();
  const tabs = React.useRef<Array<HTMLButtonElement | null>>([]);
  // The phone accordion's one bit of its own: the chosen row folded shut. Only
  // ever read below `lg`, where the panel is under the row; beside the list the
  // chosen answer is always showing.
  const [shut, setShut] = React.useState(false);

  if (items.length === 0) return null;

  // Whether the panel is under the row rather than beside the list: the phone
  // chevron is drawn exactly then, so the CSS breakpoint answers and no width
  // is repeated here.
  const panelUnder = (row: HTMLElement) => {
    const chevron = row.querySelector('[data-accordion-chevron]');
    return chevron !== null && getComputedStyle(chevron).display !== 'none';
  };

  const selectable = Boolean(onSelect);
  // Only the chosen row's sentence open, under its title.
  const expandActive = selectable && expand === 'active';

  // Asked once, of the whole list. See the note above on why this is not
  // per-row.
  const hasTitles = items.some((item) => item.title);
  const hasDescriptions = items.some((item) => item.description);

  const columns = expandActive
    ? indicator
      ? COLUMNS.expandActiveIndicator
      : COLUMNS.expandActive
    : indicator
    ? hasTitles
      ? hasDescriptions
        ? COLUMNS.titlesIndicator
        : COLUMNS.titlesOnlyIndicator
      : COLUMNS.plainIndicator
    : hasTitles
      ? COLUMNS.titles
      : COLUMNS.plain;

  // The keys a vertical tablist answers to. Selection follows focus — the
  // panel changes as the arrow moves — because the panels are already on the
  // page and switching costs nothing, so there is no reason to make a keyboard
  // user press Enter for what a mouse user gets on a click.
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (!onSelect) return;
    const last = items.length - 1;
    const current = activeIndex ?? 0;
    const next =
      event.key === 'ArrowDown'
        ? current === last ? 0 : current + 1
        : event.key === 'ArrowUp'
          ? current === 0 ? last : current - 1
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? last
              : null;
    if (next === null) return;
    event.preventDefault();
    setShut(false);
    onSelect(next);
    tabs.current[next]?.focus();
  };

  const list = (
    // The rule belongs to the top of each row, plus one on the list itself to
    // close the last one. A rule on the bottom of each row instead would leave
    // the set opening on nothing and closing on an edge.
    // `auto-rows-fr` is what makes every row the height of the tallest one.
    // The rows are tracks of this grid rather than plain blocks in a list, and
    // an `fr` track in a grid with no height of its own resolves to the largest
    // row's content — so a row whose sentence wraps to two lines sets the
    // height, and the short ones are padded out to match instead of closing up.
    <ul
      ref={ref}
      role={selectable ? 'tablist' : undefined}
      aria-orientation={selectable ? 'vertical' : undefined}
      aria-label={selectable ? label : undefined}
      onKeyDown={selectable ? onKeyDown : undefined}
      className={cn(
        'grid list-none',
        // Not below `lg` with a panel under a row: every row would grow to the
        // open one's height.
        // Not with one row open either: every row would take the open one's
        // height, and the folded ones would be mostly empty.
        (rules === 'all' || selectable) &&
          !expandActive &&
          (renderPanel ? 'lg:auto-rows-fr' : 'auto-rows-fr'),
        rules === 'all' && !selectable && 'border-b border-hairline',
        !note && className,
      )}
    >
      {items.map((item, i) => {
        const active = selectable && i === activeIndex;
        // Open on a phone: chosen and not folded away.
        const open = active && !shut;

        const cells = (
          <>
            {/* Every cell is capped in `ch`, so the cap is a count of
                characters in that cell's own face rather than a width in pixels
                that only suits one of them: 14 mono characters is "$150 /
                block" with room to spare, and 52 is about the measure a
                sentence can run to before it is hard to come back to the start
                of the next line. Without a cap an `auto` track grows to its
                content and one long label takes the row. */}
            <span
              className={cn(
                'max-w-[14ch] font-mono text-xs',
                markerWeight === 'medium' && 'font-medium',
                // With a glyph, the cap is on the words, not the glyph beside them.
                item.icon && 'inline-flex max-w-none items-center gap-2',
                active ? 'text-brand-text' : ACCENT[accent],
              )}
            >
              {item.icon && (
                // Decorative: the marker beside it says the same thing in words.
                <item.icon aria-hidden="true" className="size-3.5 shrink-0" />
              )}
              {item.marker}
            </span>

            {/* Rendered even when this row has no title, so the row still spans
                the three columns the list agreed on and its sentence stays in
                the same column as everyone else's. */}
            {hasTitles && (
              <p
                className={cn(
                  'font-bold tracking-tight',
                  TITLE_SIZE[titleSize],
                  active ? 'text-brand-text' : 'text-ink',
                  selectable &&
                    'transition-colors duration-(--duration-fast) ease-standard group-hover:text-brand-text',
                )}
              >
                {item.title}
              </p>
            )}

            {item.description && expandActive && (
              // Opened on the row track rather than to a max-height, so it
              // opens to exactly the sentence's height however many lines it
              // wraps to.
              //
              // The row closing and the row opening move in lockstep: same
              // duration, same curve, no delay on either. So one gives up
              // exactly the height the other takes, the list holds its height
              // through the swap, and nothing beside or below it (a photo
              // stretched to match it, a button under it) moves. A delay on
              // the close would leave both open for a moment and the list
              // would bulge. Only the sentence's own fade is staggered.
              <span
                className={cn(
                  'col-start-2 grid transition-[grid-template-rows,margin-top] duration-(--duration-base) ease-standard',
                  'motion-reduce:transition-none',
                  active ? 'mt-1 grid-rows-[1fr]' : 'mt-0 grid-rows-[0fr]',
                )}
              >
                <span className="min-h-0 overflow-hidden">
                  <span
                    className={cn(
                      'block max-w-[52ch] text-base text-ink-muted transition-[opacity,translate] ease-standard',
                      'motion-reduce:transition-none',
                      active
                        ? 'translate-y-0 opacity-100 duration-(--duration-base) delay-(--duration-handoff)'
                        : 'translate-y-2.5 opacity-0 duration-(--duration-handoff)',
                    )}
                  >
                    {renderDescription ? renderDescription(i) : item.description}
                  </span>
                </span>
              </span>
            )}

            {item.description && !expandActive && (
              // Muted when a title is carrying the row, full strength when the
              // sentence is the row. Derived rather than a prop: a `quiet` flag
              // here would be set the same way on every item in a list, which
              // is a decision about the list being asked once per row.
              //
              // Full strength is `text-current`, not `text-ink`: when the
              // sentence is the row it is the only thing to read, and naming
              // the page's ink meant it came out black on black wherever the
              // list was put on the dark panel. Inheriting takes the ink of
              // whatever the list was dropped into — `text-ink` from the body,
              // `on-panel` inside a `FeatureSplit` — which is the same colour in
              // both cases and the right one in both.
              <p
                className={cn(
                  'max-w-[52ch] text-base',
                  hasTitles ? 'text-ink-muted' : 'text-current',
                  // A description under a row that keeps its columns on a
                  // phone would otherwise be squeezed into the marker's column.
                  indicator && 'max-sm:col-span-full',
                  // `justify-self-end` moves the cell to the far edge of its
                  // column; `text-right` keeps a value that wraps flush with it.
                  align === 'end' && 'sm:justify-self-end sm:text-right',
                )}
              >
                {renderDescription ? renderDescription(i) : item.description}
              </p>
            )}

            {indicator && (
              // Pinned to the last column of the first row, so a row with no
              // description does not let it fall into the sentence's column.
              // One fixed box for both states, so choosing a row fills the
              // circle in place rather than changing the row's height.
              <span
                aria-hidden="true"
                className={cn(
                  'row-start-1 grid size-12 place-items-center self-center rounded-full -col-end-1',
                  'transition-colors duration-(--duration-fast) ease-standard',
                  (renderPanel ? open : active) ? 'bg-brand-strong text-on-brand-strong' : 'text-ink-muted',
                  renderPanel && active && !open && 'lg:bg-brand-strong lg:text-on-brand-strong',
                )}
              >
                {renderPanel && (
                  // The phone's accordion chevron, turned up when its panel is open.
                  <LuChevronDown
                    data-accordion-chevron=""
                    className={cn(
                      'size-5 transition-transform duration-(--duration-fast) ease-standard lg:hidden',
                      open && 'rotate-180',
                    )}
                  />
                )}
                <span className={cn('contents', renderPanel && 'max-lg:hidden')}>
                  {active ? <LuArrowRight className="size-5" /> : <LuArrowUpRight className="size-5" />}
                </span>
              </span>
            )}
          </>
        );

        const grid = cn(
          // Tighter across when there is an arrow: its circle is a column of
          // its own, and at the full gap the row spends more on space than on
          // the display-sized title it exists to show.
          // No row gap when the sentence folds under the title: the gap would
          // sit above the opening row's own margin, and under every folded
          // title as empty space. The opening row's margin is the only air.
          'grid',
          expandActive ? 'gap-y-0' : 'gap-y-2',
          indicator ? 'gap-x-6' : 'gap-x-10',
          spacing === 'relaxed' ? 'py-6 sm:py-8' : 'py-2.5 sm:py-3',
          // Stacked on a phone: three columns across 375px leaves the sentence
          // about 150px wide, which is four words a line.
          //
          // The label columns are sized by what is in them and the sentence
          // takes the rest. A share — the `1fr` the title column used to be —
          // is a claim on the row that holds whether or not there is anything
          // to put in it: a list of one-word labels was handed a third of every
          // row to set them in, and the sentence beside them wrapped to pay
          // for it.
          //
          // `minmax(0, …)` on the sentence rather than a bare fraction, because
          // a grid track's default minimum is its content's minimum width —
          // one long word would push the column past its share and pull the
          // whole list out of line.
          columns,
          // First baselines, not box tops. The marker is 12px and the title
          // 18px or more, so aligning their boxes would leave the marker floating above
          // the line it belongs to.
          //
          // Except beside an arrow. There every part is one line — marker,
          // question, arrow — and a 12px marker on a display title's baseline
          // sits down at its foot; centred, the three read as one row.
          indicator ? 'items-center' : 'sm:items-baseline',
        );

        return (
          <li
            key={`${item.marker ?? ''}-${item.title ?? ''}-${i}`}
            role={selectable ? 'presentation' : undefined}
            // The nth row waits n steps, so the set arrives as one gesture
            // travelling down the list rather than all at once. The step is a
            // token multiplied here rather than a number written here — a
            // literal `70 * i` in a component would be the one duration in the
            // product that Tier 1 does not own.
            style={
              state === 'revealed'
                ? { animationDelay: `calc(var(--duration-stagger) * ${i})` }
                : undefined
            }
            className={cn(
              'relative border-hairline',
              selectable
                ? 'border-b'
                : rules === 'all'
                  ? 'border-t'
                  : 'border-t first:border-t-0 first:pt-0 last:pb-0',
              !selectable && grid,
              // `static` adds nothing at all, which is what a visitor on
              // reduced motion and a browser without the observer both get.
              state === 'armed' && 'opacity-0',
              state === 'revealed' && 'animate-reveal-up',
            )}
          >
            {selectable ? (
              <>
              {/* Its own box so the mint rule sits under the row, not under a
                  phone panel opened beneath it. */}
              {/* Full height only where the panel is beside the list: a grid
                  row resolves the percentage, so below `lg` it took the open
                  row's whole height, panel included. */}
              <div className={cn('relative', renderPanel ? 'lg:h-full' : 'h-full')}>
                <button
                  ref={(node) => {
                    tabs.current[i] = node;
                  }}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  aria-controls={item.controls}
                  // Roving tabindex: Tab lands on the chosen row and leaves the
                  // list on the next press, and the arrows move within it.
                  tabIndex={active ? 0 : -1}
                  aria-expanded={renderPanel ? open : undefined}
                  onClick={() => {
                    // Tapping the open row folds it on a phone. Beside the
                    // list the flag is never read, so a desktop click is a no-op.
                    if (renderPanel && active) {
                      setShut((s) => !s);
                      return;
                    }
                    setShut(false);
                    onSelect?.(i);
                  }}
                  // Resting a mouse on a row chooses it, exactly as a click
                  // does. `pointermove` rather than `pointerenter` so a row
                  // only answers a hand that is moving, and touch is left to
                  // the tap it already has.
                  //
                  // Not while the panel is under the row: there hovering would
                  // choose the row and the click that follows would fold it.
                  onPointerMove={(e) => {
                    if (!selectOnHover || e.pointerType !== 'mouse' || active) return;
                    if (panelUnder(e.currentTarget)) return;
                    onSelect?.(i);
                  }}
                  className={cn(
                    grid,
                    // `content-center` because `auto-rows-fr` can make the row
                    // taller than its line, and the line would otherwise sit at
                    // the top with the spare height under it. It centres the
                    // track, not the items, so the marker and title keep their
                    // shared baseline.
                    'group h-full w-full content-center text-left',
                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                    !selectOnHover &&
                      'transition-transform duration-(--duration-fast) ease-standard hover:translate-x-1.5 motion-reduce:transition-none',
                  )}
                >
                  {cells}
                </button>

                {/* The selected row's rule: a thick mint bar laid over the
                    hairline, drawn out from the left when the row is chosen.
                    Over the rule rather than instead of it, so choosing a row
                    does not change its height by the difference. */}
                <span
                  aria-hidden="true"
                  className={cn(
                    'absolute inset-x-0 -bottom-px h-0.5 origin-left bg-brand-edge',
                    'transition-transform duration-(--duration-base) ease-standard motion-reduce:transition-none',
                    active ? 'scale-x-100' : 'scale-x-0',
                  )}
                />
              </div>

              {renderPanel && active && (
                // Opens and closes on the row track, as the values do.
                <div
                  className={cn(
                    'grid transition-[grid-template-rows] duration-(--duration-base) ease-standard lg:hidden',
                    'motion-reduce:transition-none',
                    open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                  )}
                >
                  <div className="min-h-0 overflow-hidden">
                    <div className="pt-4 pb-6">{renderPanel(i)}</div>
                  </div>
                </div>
              )}
              </>
            ) : (
              cells
            )}
          </li>
        );
      })}
    </ul>
  );

  if (!note) return list;

  return (
    <div className={className}>
      {list}
      <p className="mt-3 text-sm text-ink-muted">{note}</p>
    </div>
  );
}
